import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { EmployeeWithIssues, ValidationIssue } from '@/types/payroll-validator';
import { EmployeeIssuesExpanded } from './EmployeeIssuesExpanded';
import ApiService from '@/services/api';

interface EmployeeIssuesGridProps {
  employees: EmployeeWithIssues[];
  sessionId: string;
}

export const EmployeeIssuesGrid: React.FC<EmployeeIssuesGridProps> = ({
  employees,
  sessionId,
}) => {
  const [expandedEmployeeId, setExpandedEmployeeId] = useState<string | null>(null);
  const [employeeIssues, setEmployeeIssues] = useState<Record<string, ValidationIssue[]>>({});
  const [loadingEmployeeId, setLoadingEmployeeId] = useState<string | null>(null);

  const handleRowClick = async (employeeId: string) => {
    // If clicking the same row, collapse it
    if (expandedEmployeeId === employeeId) {
      setExpandedEmployeeId(null);
      return;
    }

    // Expand the row
    setExpandedEmployeeId(employeeId);

    // If we already have the issues cached, don't refetch
    if (employeeIssues[employeeId]) {
      return;
    }

    // Fetch issues for this employee
    setLoadingEmployeeId(employeeId);
    try {
      const response = await ApiService.request<{ issues: ValidationIssue[] }>(
        "getValidationIssues",
        {
          urlParams: { sessionId },
          queryParams: { employee_id: employeeId },
          skipAuth: true,
        }
      );

      setEmployeeIssues((prev) => ({
        ...prev,
        [employeeId]: response.issues || [],
      }));
    } catch (error) {
      console.error('Failed to fetch employee issues:', error);
      setEmployeeIssues((prev) => ({
        ...prev,
        [employeeId]: [],
      }));
    } finally {
      setLoadingEmployeeId(null);
    }
  };

  const getEmployeeName = (employee: EmployeeWithIssues) => {
    if (employee.first_name && employee.last_name) {
      return `${employee.first_name} ${employee.last_name}`;
    }
    if (employee.first_name) return employee.first_name;
    if (employee.last_name) return employee.last_name;
    return 'Unknown';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl font-bold">Employees</CardTitle>
        <p className="text-sm text-gray-600 mt-1">
          Click on an employee row to view their validation issues
        </p>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-12"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>NI Number</TableHead>
                <TableHead className="text-center">Critical</TableHead>
                <TableHead className="text-center">Warning</TableHead>
                <TableHead className="text-center">Info</TableHead>
                <TableHead className="text-center">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No employees found
                  </TableCell>
                </TableRow>
              ) : (
                employees.map((employee) => {
                  const isExpanded = expandedEmployeeId === employee.employee_id;
                  const isLoading = loadingEmployeeId === employee.employee_id;
                  const issues = employeeIssues[employee.employee_id] || [];

                  return (
                    <React.Fragment key={employee.employee_id}>
                      <TableRow
                        className="cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => handleRowClick(employee.employee_id)}
                      >
                        <TableCell>
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-500" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-500" />
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          {getEmployeeName(employee)}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {employee.ni_number || 'N/A'}
                        </TableCell>
                        <TableCell className="text-center">
                          {employee.critical_issues > 0 ? (
                            <Badge variant="destructive" className="text-xs">
                              {employee.critical_issues}
                            </Badge>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {employee.warning_issues > 0 ? (
                            <Badge className="bg-orange-100 text-orange-800 text-xs">
                              {employee.warning_issues}
                            </Badge>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {employee.info_issues > 0 ? (
                            <Badge variant="outline" className="text-xs">
                              {employee.info_issues}
                            </Badge>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center font-semibold">
                          {employee.total_issues}
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={7} className="p-0">
                            {isLoading ? (
                              <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                                <span className="ml-2 text-gray-600">Loading issues...</span>
                              </div>
                            ) : (
                              <EmployeeIssuesExpanded issues={issues} />
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

